import type { TreeNode } from '@/components/codebook-tree-browser'

export const CODEBOOK_FILE_EXTENSIONS = ['.xlsx', '.xls', '.xlsm', '.csv'] as const

export function isCodebookFileName(name: string): boolean {
  const lower = name.toLowerCase()
  return CODEBOOK_FILE_EXTENSIONS.some(ext => lower.endsWith(ext))
}

/** Drop READMEs and other non-codebook files; prune empty folders. */
export function filterCodebookTree(nodes: TreeNode[]): TreeNode[] {
  const filtered: TreeNode[] = []

  for (const node of nodes) {
    if (node.type === 'file') {
      if (isCodebookFileName(node.name)) filtered.push(node)
      continue
    }
    const children = filterCodebookTree(node.children)
    if (children.length > 0) {
      filtered.push({ ...node, children })
    }
  }

  return filtered
}

export interface FlatCodebookFile {
  id: string
  name: string
  size?: number
  last_modified: string
}

/** Reconstruct a folder tree from blob-style slash-separated paths. */
export function buildCodebookTreeFromFlatFiles(files: FlatCodebookFile[]): TreeNode[] {
  type MutableNode = TreeNode & { _map?: Record<string, MutableNode> }
  const root: Record<string, MutableNode> = {}

  for (const file of files) {
    const parts = file.name.split('/')
    let current = root
    let pathSoFar = ''

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i]
      if (!part || part === '.folder') break

      pathSoFar = pathSoFar ? `${pathSoFar}/${part}` : part
      const isLast = i === parts.length - 1

      if (!current[part]) {
        current[part] = isLast
          ? {
              id: file.id,
              name: part,
              type: 'file',
              path: file.name,
              size: file.size,
              last_modified: file.last_modified,
              children: [],
            }
          : {
              id: `folder:${pathSoFar}`,
              name: part,
              type: 'folder',
              path: pathSoFar,
              last_modified: '',
              children: [],
              _map: {},
            }
      }

      if (!isLast) {
        if (!current[part]._map) current[part]._map = {}
        current = current[part]._map!
      }
    }
  }

  const toList = (mapping: Record<string, MutableNode>): TreeNode[] => {
    const nodes = Object.values(mapping).map(node => {
      const childMap = node._map ?? {}
      delete node._map
      node.children = toList(childMap)
      return node
    })
    nodes.sort((a, b) =>
      a.type === b.type
        ? a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        : a.type === 'folder' ? -1 : 1,
    )
    return nodes
  }

  return toList(root)
}

export function countCodebookFiles(node: TreeNode): number {
  if (node.type === 'file') return 1
  return node.children.reduce((sum, child) => sum + countCodebookFiles(child), 0)
}

function nodeMatchesQuery(node: TreeNode, needle: string): boolean {
  return (
    node.name.toLowerCase().includes(needle) ||
    node.path.toLowerCase().includes(needle)
  )
}

/**
 * Filter a codebook tree by search query (option A): keep matching files and
 * any folder that matches or still has matching descendants. Empty query
 * returns the tree unchanged.
 */
export function filterCodebookTreeBySearch(
  nodes: TreeNode[],
  query: string,
): TreeNode[] {
  const needle = query.trim().toLowerCase()
  if (!needle) return nodes

  const filtered: TreeNode[] = []
  for (const node of nodes) {
    if (node.type === 'file') {
      if (nodeMatchesQuery(node, needle)) filtered.push(node)
      continue
    }
    const children = filterCodebookTreeBySearch(node.children, needle)
    if (children.length > 0 || nodeMatchesQuery(node, needle)) {
      filtered.push({
        ...node,
        children:
          children.length > 0
            ? children
            : // Folder name matched but no child matches — keep structure so
              // the user can expand into the full folder contents.
              node.children,
      })
    }
  }
  return filtered
}

/** Count files visible in a (possibly search-filtered) tree. */
export function countFilesInTree(nodes: TreeNode[]): number {
  return nodes.reduce((sum, node) => sum + countCodebookFiles(node), 0)
}

/** Expand every folder so nested study/version paths are visible without hunting. */
export function allFolderIds(tree: TreeNode[]): Set<string> {
  const ids = new Set<string>()
  const walk = (nodes: TreeNode[]) => {
    for (const node of nodes) {
      if (node.type === 'folder') {
        ids.add(node.id)
        walk(node.children)
      }
    }
  }
  walk(tree)
  return ids
}

/** Root folders + ancestors of the selected file — open on load. */
export function defaultExpandedFolderIds(
  tree: TreeNode[],
  selectedFileId?: string,
): Set<string> {
  const expanded = allFolderIds(tree)

  if (!selectedFileId) return expanded

  const walk = (nodes: TreeNode[], ancestors: string[]): boolean => {
    for (const node of nodes) {
      if (node.type === 'file') {
        if (node.id === selectedFileId) {
          for (const id of ancestors) expanded.add(id)
          return true
        }
        continue
      }
      if (walk(node.children, [...ancestors, node.id])) return true
    }
    return false
  }

  walk(tree, [])
  return expanded
}
